import { Group, Text, Tooltip } from "@mantine/core"
import { IconInfoCircle } from "@tabler/icons-react"
import { createColumnHelper, DisplayColumnDef } from "@tanstack/react-table"
import { getCheckboxColumnDefs, TextCell } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  BatchUpdateOperationItem,
  ComponentOperationItem,
  MaterialOperationItem,
  ProductOperationItem,
} from "@tfg/types/schemas/modules/traceability/queries/find-batch-update-operations.response.dto"

import { CopyToClipboard } from "~/components/CopyToClipboard.tsx"
import { ProductionSiteDisplay } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProductionSiteDisplay.tsx"
import { useAuth } from "~/store/auth-context.tsx"

type Props = {
  batchOperations: BatchUpdateOperationItem[]
  key: string
}

export function useBatchColumns({
  batchOperations,
  key,
}: Props): DisplayColumnDef<BatchUpdateOperationItem, unknown>[] {
  const columnHelper = createColumnHelper<BatchUpdateOperationItem>()
  const { isAdmin } = useAuth()
  const { t } = useTranslation(["traceability"])

  return useMemo(() => {
    const customFieldColumns: DisplayColumnDef<
      BatchUpdateOperationItem,
      unknown
    >[] = []
    if (batchOperations[0]?.customFieldsValues) {
      Object.keys(batchOperations[0]?.customFieldsValues ?? {}).map((key) => {
        customFieldColumns.push(
          columnHelper.display({
            id: key,
            header: key,
            cell: (info) => {
              if (!info.row.original.customFieldsValues) {
                return null
              }
              return (
                <TextCell
                  value={
                    info.row.original.customFieldsValues[key]?.value ??
                    undefined
                  }
                />
              )
            },
          }),
          columnHelper.display({
            id: `${key} document name`,
            header: t("batchModal.labels.documentProof"),
            cell: (info) => {
              const customField = info.row.original.customFieldsValues?.[key]
              if (!customField) {
                return null
              }
              return (
                <TextCell
                  value={
                    customField.document?.originalName ??
                    t("batchModal.noDocument")
                  }
                />
              )
            },
          })
        )
      })
    }

    const columns: DisplayColumnDef<BatchUpdateOperationItem, unknown>[] = [
      getCheckboxColumnDefs<BatchUpdateOperationItem>() as DisplayColumnDef<
        BatchUpdateOperationItem,
        unknown
      >,
      //GENERAL COLUMNS
      batchOperations.some((row) => row.informationRequestItemReference)
        ? columnHelper.display({
            id: "informationRequestItemReference",
            header: () => (
              <Group wrap="nowrap" gap={0}>
                {t("batchModal.labels.informationRequestItemReference")}
                <Tooltip
                  label={t(
                    "batchModal.labels.informationRequestItemReferenceHint"
                  )}
                  multiline={true}
                >
                  <IconInfoCircle />
                </Tooltip>
              </Group>
            ),
            cell: (info) => (
              <Text>{info.row.original.informationRequestItemReference}</Text>
            ),
          })
        : null,
      batchOperations.some((row) => row.productName)
        ? columnHelper.display({
            header: t("batchModal.labels.productName"),
            cell: (info) => (
              <TextCell value={info.row.original.productName ?? undefined} />
            ),
          })
        : null,
      batchOperations.some((row) => row.productReference)
        ? columnHelper.display({
            header: t("batchModal.labels.productReference"),
            cell: (info) => (
              <TextCell
                value={info.row.original.productReference ?? undefined}
              />
            ),
          })
        : null,
      batchOperations.some((row) => row.contact?.email)
        ? columnHelper.display({
            header: t("batchModal.labels.contact"),
            cell: (info) => (
              <TextCell value={info.row.original.contact?.email ?? undefined} />
            ),
          })
        : null,
      columnHelper.accessor("productionSiteId", {
        header: t("batchModal.labels.productionSite"),
        cell: (info) => {
          const productionSiteId = info.getValue()
          if (!productionSiteId) return null
          return <ProductionSiteDisplay productionSiteId={productionSiteId} />
        },
      }),
      columnHelper.display({
        header: t("batchModal.labels.clientName"),
        cell: (info) => (
          <TextCell value={info.row.original.clientName ?? undefined} />
        ),
      }),
      ...(key === "reference"
        ? [
            columnHelper.display({
              header: t("batchModal.labels.reference"),
              cell: (info) => (
                <TextCell value={info.row.original.reference ?? undefined} />
              ),
            }),
          ]
        : []),
      ...(isAdmin
        ? [
            columnHelper.display({
              header: t("batchModal.labels.productId"),
              cell: (info) => (
                <CopyToClipboard
                  text={info.row.original.productId?.toString() ?? ""}
                  color="violet"
                />
              ),
            }),
          ]
        : []),
      //PRODUCT SPECIFIC COLUMNS
      batchOperations.some(
        (row) => isProductOperationItem(row) && row.productCollection
      )
        ? columnHelper.display({
            header: t("batchModal.labels.productCollection"),
            cell: (info) =>
              isProductOperationItem(info.row.original) && (
                <TextCell
                  value={info.row.original.productCollection ?? undefined}
                />
              ),
          })
        : null,
      batchOperations.some(
        (row) => isProductOperationItem(row) && row.productCollection
      )
        ? columnHelper.display({
            header: t("batchModal.labels.productColor"),
            cell: (info) =>
              isProductOperationItem(info.row.original) && (
                <TextCell value={info.row.original.productColor ?? undefined} />
              ),
          })
        : null,
      batchOperations.some(
        (row) => isProductOperationItem(row) && row.productCollection
      )
        ? columnHelper.display({
            header: t("batchModal.labels.purchaseOrder"),
            cell: (info) =>
              isProductOperationItem(info.row.original) && (
                <TextCell
                  value={info.row.original.purchaseOrder ?? undefined}
                />
              ),
          })
        : null,
      batchOperations.some(
        (row) => isComponentOperationItem(row) && row.componentReference
      )
        ? columnHelper.display({
            header: t("batchModal.labels.componentReference"),
            cell: (info) =>
              isComponentOperationItem(info.row.original) && (
                <TextCell
                  value={info.row.original.componentReference ?? undefined}
                />
              ),
          })
        : null,
      batchOperations.some(
        (row) => isComponentOperationItem(row) && row.componentName
      )
        ? columnHelper.display({
            header: t("batchModal.labels.componentName"),
            cell: (info) =>
              isComponentOperationItem(info.row.original) && (
                <Text>{info.row.original.componentName}</Text>
              ),
          })
        : null,
      batchOperations.some(
        (row) => isMaterialOperationItem(row) && row.materialReference
      )
        ? columnHelper.display({
            id: "materialReference",
            header: t("batchModal.labels.materialReference"),
            cell: (info) =>
              isMaterialOperationItem(info.row.original) && (
                <Text>{info.row.original.materialReference}</Text>
              ),
          })
        : null,
      batchOperations.some(
        (row) => isMaterialOperationItem(row) && row.materialName
      )
        ? columnHelper.display({
            id: "materialReference",
            header: t("batchModal.labels.materialName"),
            cell: (info) =>
              isMaterialOperationItem(info.row.original) && (
                <TextCell value={info.row.original.materialName ?? undefined} />
              ),
          })
        : null,
      batchOperations.some(
        (row) => isMaterialOperationItem(row) && row.materialReference
      )
        ? columnHelper.display({
            id: "materialProportion",
            header: t("batchModal.labels.materialProportion"),
            cell: (info) =>
              isMaterialOperationItem(info.row.original) && (
                <TextCell
                  value={
                    info.row.original.materialProportion.toString() ?? undefined
                  }
                />
              ),
          })
        : null,
      batchOperations.some((row) => row.purchaseOrderRef)
        ? columnHelper.display({
            header: t("batchModal.labels.purchaseOrderRef"),
            cell: (info) => (
              <TextCell
                value={info.row.original.purchaseOrderRef ?? undefined}
              />
            ),
          })
        : null,
    ].filter(Boolean) // this filters any null values for conditional rendering,
    return [...columns, ...customFieldColumns]
  }, [batchOperations, columnHelper, t, key, isAdmin])
}

function isComponentOperationItem(
  item: BatchUpdateOperationItem
): item is ComponentOperationItem {
  return "componentReference" in item
}

function isProductOperationItem(
  item: BatchUpdateOperationItem
): item is ProductOperationItem {
  return "productCollection" in item
}

function isMaterialOperationItem(
  item: BatchUpdateOperationItem
): item is MaterialOperationItem {
  return "materialReference" in item
}
